import {
  ActionIcon,
  Button,
  Card,
  Container,
  createStyles,
  Group,
  ScrollArea,
  Text,
  useMantineTheme
} from '@mantine/core'
import { IconChevronLeft, IconChevronRight, IconPlus } from '@tabler/icons'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PictureCard } from '@/components/surfaces'
import { StudentModal } from '@/containers/navigation/StudentMenu'
import { MultipleStudentChannelProfilesModal } from '@/containers/students/StudentsView/components'
import { useFetch, useStudent } from '@/lib/hooks'
import { getSession, getUrlString, setSession } from '@/lib/utils'
import { useNav } from '@/providers/NavProvider'
import {
  Channel as ChannelType,
  Site as SiteType,
  StudentChannelProfile as StudentChannelProfileType,
  StudentSiteProfile as StudentSiteProfileType
} from '@/types'

interface Props {
  siteData: SiteType
  channelData: ChannelType
}

const useStyles = createStyles(theme => ({
  nav: {
    position: 'relative',
    width: '100%',
    height: 'auto',
    backgroundColor: theme.white
  },
  card: {
    cursor: 'pointer',
    minWidth: '160px',
    padding: '5px 10px 5px 5px !important',
    '&:hover': {
      backgroundColor: theme.colors.gray[2],
      '& div': { color: 'inherit' }
    }
  },
  selectedCard: {
    backgroundColor: theme.colors.blue[6],
    '& div': { color: theme.white }
  },
  scrollAreaWrapper: { position: 'relative' },
  scrollAction: {
    border: `1px solid ${theme.white}`,
    position: 'absolute',
    top: '5px',
    zIndex: 2,
    boxShadow: theme.shadows.sm
  },
  scrollLeftAction: { left: theme.spacing.xs },
  scrollRightAction: { right: theme.spacing.xs }
}))

export default function StudentNav({ siteData, channelData }: Props) {
  // Hooks
  const router = useRouter()
  const theme = useMantineTheme()
  const { classes } = useStyles()
  const {
    studentModalOpen,
    setStudentModalOpen,
    studentModalOpenFromBuyButton,
    setStudentModalOpenFromBuyButton
  } = useNav()
  const { mutate: mutateStudent } = useStudent()
  const { site } = router?.query || {}
  const siteSlug = getUrlString(site)

  // Constants
  const sessionStudentUid = getSession('stuid')
  const isSitePrivate = siteData?.privacy === 'private'

  // States
  const [multipleStudentChannelProfiles, setMultipleStudentChannelProfiles] =
    useState<StudentSiteProfileType | null>(null)
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Refs
  const viewportRef = useRef<HTMLDivElement | null>(null)

  // Fetch
  const { data: studentsData } = useFetch(
    [siteSlug ? `/${siteSlug}/students/` : null, { page_size: 500, is_active: true }],
    { errorRetryInterval: 300, errorRetryCount: 5 }
  )
  const { results: students = [] } = studentsData || {}
  const sortedStudents = useMemo(
    () =>
      students.sort(
        (a: StudentSiteProfileType, b: StudentSiteProfileType) =>
          (b.uid === sessionStudentUid ? 1 : 0) - (a.uid === sessionStudentUid ? 1 : 0)
      ),
    [students, sessionStudentUid]
  )

  // Actions
  const setStudent = useCallback(
    async (studentUid: string) => {
      setSession('stuid', studentUid)
      await mutateStudent()
    },
    [mutateStudent]
  )

  const handleStudentChannelProfileRedirect = (
    studentChannelProfile: StudentChannelProfileType,
    studentSiteProfileUid: string
  ) => {
    setStudent(studentSiteProfileUid)
    const channelSlug = studentChannelProfile?.channelProfile?.channel?.slug || ''
    const storeSlug = studentChannelProfile?.showcase?.store?.slug || ''
    const showcaseSlug = studentChannelProfile?.showcase?.slug || ''
    setMultipleStudentChannelProfiles(null)
    router.push(['/', channelSlug, storeSlug, showcaseSlug].filter(Boolean).join('/'))
  }

  const handleStudentSiteProfileSelect = (studentSiteProfile: StudentSiteProfileType) => {
    const { studentChannelProfiles = [] } = studentSiteProfile || {}
    const studentChannelProfilesExists =
      Array.isArray(studentChannelProfiles) && studentChannelProfiles.length > 0

    if (!studentChannelProfilesExists) {
      setStudent(studentSiteProfile.uid)
      router.push('/accounts/home')
    } else {
      const isUniqueStudentChannelProfile = studentChannelProfiles.length === 1
      return isUniqueStudentChannelProfile
        ? handleStudentChannelProfileRedirect(studentChannelProfiles[0], studentSiteProfile.uid)
        : setMultipleStudentChannelProfiles(studentSiteProfile)
    }
  }

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current

    if (!viewport) {
      setHasHorizontalOverflow(false)
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }

    const { clientWidth, scrollLeft, scrollWidth } = viewport
    const hasOverflow = scrollWidth > clientWidth + 1

    setHasHorizontalOverflow(hasOverflow)
    setCanScrollLeft(hasOverflow && scrollLeft > 1)
    setCanScrollRight(hasOverflow && scrollLeft + clientWidth < scrollWidth - 1)
  }, [])

  const handleHorizontalScroll = useCallback((direction: 'left' | 'right') => {
    viewportRef.current?.scrollBy({
      left: direction === 'left' ? -260 : 260,
      behavior: 'smooth'
    })
  }, [])

  useEffect(() => {
    updateScrollState()

    const viewport = viewportRef.current

    if (!viewport) return undefined

    const handleScroll = () => updateScrollState()
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateScrollState()) : null

    viewport.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', updateScrollState)
    resizeObserver?.observe(viewport)

    if (viewport.firstElementChild) {
      resizeObserver?.observe(viewport.firstElementChild)
    }

    return () => {
      viewport.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', updateScrollState)
      resizeObserver?.disconnect()
    }
  }, [sortedStudents.length, isSitePrivate, updateScrollState])

  return (
    <nav className={classes.nav}>
      <Container size="xl" pb={0} pt={5}>
        <Text fw="bold" size="xs" tt="uppercase">
          Alunos desta loja
        </Text>
        <div className={classes.scrollAreaWrapper}>
          {hasHorizontalOverflow && canScrollLeft && (
            <ActionIcon
              color="custom"
              radius="md"
              variant="filled"
              aria-label="Rolar lista de alunos para a esquerda"
              className={`${classes.scrollAction} ${classes.scrollLeftAction}`}
              onClick={() => handleHorizontalScroll('left')}>
              <IconChevronLeft />
            </ActionIcon>
          )}
          {hasHorizontalOverflow && canScrollRight && (
            <ActionIcon
              color="custom"
              radius="md"
              variant="filled"
              aria-label="Rolar lista de alunos para a direita"
              className={`${classes.scrollAction} ${classes.scrollRightAction}`}
              onClick={() => handleHorizontalScroll('right')}>
              <IconChevronRight />
            </ActionIcon>
          )}
          <ScrollArea offsetScrollbars viewportRef={viewportRef}>
            <Group noWrap spacing="xs">
              {sortedStudents.map((student: StudentSiteProfileType) => {
                const isSelected = student.uid === sessionStudentUid
                return (
                  <Card
                    key={student.uid}
                    withBorder
                    shadow="xs"
                    className={`${classes.card}${isSelected ? ` ${classes.selectedCard}` : ''}`}
                    onClick={() => handleStudentSiteProfileSelect(student)}>
                    <PictureCard
                      title={`${student.firstName} ${student.lastName}`}
                      picture={student?.picture}
                      subtitle={
                        <Text fw={isSelected ? 'bold' : 'normal'} fz="xs">
                          {isSelected ? 'Comprando para' : 'Selecionar'}
                        </Text>
                      }
                      subtitlePosition="before"
                      subtitleHiddenXs={false}
                      disableTooltip
                      size="small"
                    />
                  </Card>
                )
              })}
              <Button
                leftIcon={<IconPlus />}
                hidden={isSitePrivate}
                sx={{ boxShadow: theme.shadows.xs, height: '45px' }}
                onClick={() => setStudentModalOpen?.(true)}>
                Adicionar
              </Button>
            </Group>
          </ScrollArea>
        </div>
      </Container>
      <StudentModal
        open={studentModalOpen || studentModalOpenFromBuyButton || false}
        onClose={() => {
          setStudentModalOpen?.(false)
          setStudentModalOpenFromBuyButton?.(false)
        }}
        siteData={siteData}
        channelData={channelData}
        startCreateForm={studentModalOpen || false}
      />
      <MultipleStudentChannelProfilesModal
        data={multipleStudentChannelProfiles}
        onSelect={handleStudentChannelProfileRedirect}
        onHomeRedirect={() => {
          if (multipleStudentChannelProfiles?.uid) setStudent(multipleStudentChannelProfiles.uid)
          router.push('/accounts/home')
        }}
        onClose={() => setMultipleStudentChannelProfiles(null)}
      />
    </nav>
  )
}
